import Uploading from "components/Uploading";
import { WISDOM_PMS_ROLE_PERMISSION } from "constants/constants";
import React, { useEffect, useMemo, useState } from "react";
import { fetchWisdomWorkEnvDashboard } from "services/wisdom/wisdomData";
import { roundNumber } from "utils/roundNumber";

const WEAK_THRESHOLD = 70;
const SOURCE_COLORS = ["#8B7CE8", "#2E93CC", "#E0704F", "#22B07D", "#E0A32B", "#D8543F", "#5FE39D", "#B09509"];
// leadership/oversight roles vs. day-to-day staff — drives the management-vs-staff gap section
const MANAGEMENT_ROLES = new Set(["AC", "EX", "HOD", "QA", "ML", "OEL"]);

const scoreColor = (v) => v >= 90 ? "#22B07D" : v >= 80 ? "#2E93CC" : v >= 70 ? "#E0A32B" : "#D8543F";
const fmt = (v) => v == null ? "—" : `${roundNumber(v)}%`;
const avg = (bucket) => (bucket && bucket.count) ? bucket.sum / bucket.count : null;

const employeeFullName = (employee) =>
    [employee?.first_name, employee?.middle_name, employee?.last_name].filter(Boolean).join(" ") || "بدون اسم";

const WorkEnvDashboard = ({ orgId, year, month }) => {
    const [fetching, setFetching] = useState(true);
    const [dashboard, setDashboard] = useState([]);
    const [selectedSource, setSelectedSource] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetchWisdomWorkEnvDashboard(orgId, year, month);
                setDashboard(response || []);
            } catch (err) {
                console.error("error:", err);
            } finally {
                setFetching(false);
            }
        };

        load();
    }, [orgId, year, month]);

    const data = useMemo(() => {
        const sources = {};
        const axes = {};
        const bySourceCount = {};
        const bySourceReport = {};
        const bySourceAxis = {};
        const bySourceForms = {};
        let processedCount = 0;

        dashboard.forEach((report) => {
            const results = report.results || [];
            if (!results.length) return;

            const form = results[0]?.questionResult?.sub_field?.field?.form;
            const formType = (form?.code || "").split(" | ")[1];
            if (formType && formType !== "W") return;
            const roleCode = (form?.code || "").split(" | ")[0] || "?";
            const roleTitle = report.employee?.employee?.role?.title?.trim();
            const sourceKey = roleTitle || WISDOM_PMS_ROLE_PERMISSION[roleCode] || roleCode;

            if (!sources[sourceKey]) {
                sources[sourceKey] = {
                    key: sourceKey,
                    label: sourceKey,
                    color: SOURCE_COLORS[Object.keys(sources).length % SOURCE_COLORS.length],
                    mgmt: MANAGEMENT_ROLES.has(roleCode),
                };
            }

            processedCount += 1;

            bySourceCount[sourceKey] = (bySourceCount[sourceKey] || 0) + 1;
            bySourceReport[sourceKey] ??= { sum: 0, count: 0 };
            bySourceReport[sourceKey].sum += Number(report.avg_score) || 0;
            bySourceReport[sourceKey].count += 1;

            bySourceForms[sourceKey] ??= [];
            bySourceForms[sourceKey].push({
                id: report.id ?? `${sourceKey}-${bySourceForms[sourceKey].length}`,
                name: employeeFullName(report.employee?.employee),
                score: Number(report.avg_score) || 0,
                date: report.createdAt || report.created_at || null,
            });

            const axisSums = {};
            results.forEach((r) => {
                const field = r.questionResult?.sub_field?.field;
                if (!field || r.score == null || !r.questionResult?.max_score) return;

                const axisKey = field.ar_name || field.en_name || `field-${field.id}`;
                axes[axisKey] ??= { key: axisKey, label: axisKey };
                axisSums[axisKey] ??= { sum: 0, count: 0 };
                axisSums[axisKey].sum += (Number(r.score) / Number(r.questionResult.max_score)) * 100;
                axisSums[axisKey].count += 1;
            });

            Object.entries(axisSums).forEach(([axisKey, bucket]) => {
                const axisScore = bucket.sum / bucket.count;
                bySourceAxis[sourceKey] ??= {};
                bySourceAxis[sourceKey][axisKey] ??= { sum: 0, count: 0 };
                bySourceAxis[sourceKey][axisKey].sum += axisScore;
                bySourceAxis[sourceKey][axisKey].count += 1;
            });
        });

        const sourceList = Object.values(sources);
        const axisList = Object.values(axes);

        const sourceAvg = {};
        sourceList.forEach((s) => { sourceAvg[s.key] = avg(bySourceReport[s.key]); });

        const weightedAxisAvg = (axisKey, filterFn = () => true) => {
            let t = 0, w = 0;
            sourceList.filter(filterFn).forEach((s) => {
                const v = avg(bySourceAxis[s.key]?.[axisKey]);
                const n = bySourceCount[s.key] || 0;
                if (v != null && n) { t += v * n; w += n; }
            });
            return w ? t / w : null;
        };

        const axisWavg = {};
        axisList.forEach((a) => { axisWavg[a.key] = weightedAxisAvg(a.key); });

        const axisListSorted = axisList.slice().sort(
            (a, b) => (axisWavg[b.key] ?? -1) - (axisWavg[a.key] ?? -1)
        );

        const scoredAxes = axisListSorted.filter((a) => axisWavg[a.key] != null);
        const overall = scoredAxes.length
            ? scoredAxes.reduce((t, a) => t + axisWavg[a.key], 0) / scoredAxes.length
            : null;

        const worstAxis = scoredAxes.length
            ? scoredAxes.reduce((w, a) => (axisWavg[a.key] < axisWavg[w.key] ? a : w))
            : null;

        const mgmtSources = sourceList.filter((s) => s.mgmt);
        const staffSources = sourceList.filter((s) => !s.mgmt);

        const mgmtOverallAvg = mgmtSources.length
            ? mgmtSources.reduce((t, s) => t + (sourceAvg[s.key] ?? 0), 0) / mgmtSources.length
            : null;

        const staffOverallAvg = (() => {
            let t = 0, w = 0;
            staffSources.forEach((s) => {
                const v = sourceAvg[s.key];
                const n = bySourceCount[s.key] || 0;
                if (v != null && n) { t += v * n; w += n; }
            });
            return w ? t / w : null;
        })();

        const gaps = axisListSorted
            .map((a) => {
                const mgmtVals = mgmtSources
                    .map((s) => avg(bySourceAxis[s.key]?.[a.key]))
                    .filter((v) => v != null);
                const mgmtScore = mgmtVals.length ? mgmtVals.reduce((x, y) => x + y, 0) / mgmtVals.length : null;
                const staffScore = weightedAxisAvg(a.key, (s) => !s.mgmt);
                return {
                    key: a.key,
                    label: a.label,
                    mgmt: mgmtScore,
                    staff: staffScore,
                    gap: (mgmtScore != null && staffScore != null) ? mgmtScore - staffScore : null,
                };
            })
            .filter((g) => g.gap != null)
            .sort((a, b) => b.gap - a.gap);

        return {
            sources: sourceList,
            axes: axisListSorted,
            bySourceCount,
            sourceAvg,
            axisWavg,
            bySourceAxis,
            bySourceForms,
            totalParticipants: processedCount,
            overall,
            worstAxis,
            mgmtOverallAvg,
            staffOverallAvg,
            hasMgmtGap: mgmtSources.length > 0 && staffSources.length > 0,
            gaps,
        };
    }, [dashboard]);

    if (fetching) return <Uploading />;

    if (!data.totalParticipants) {
        return (
            <div dir="rtl" className="w-[min(92vw,700px)] p-8 text-center text-[#9DB2CE]">
                لا توجد بيانات تقييم بيئة العمل متاحة لهذه الجهة بعد.
            </div>
        );
    }

    let ringOffset = 0;

    const selectedSourceObj = selectedSource ? data.sources.find((s) => s.key === selectedSource) : null;
    const selectedForms = selectedSourceObj
        ? (data.bySourceForms[selectedSourceObj.key] || []).slice().sort((a, b) => b.score - a.score)
        : [];

    return (
        <div dir="rtl" className="w-[min(94vw,1080px)] text-[#EAF1FA]">
            <div className="mb-1">
                <h1 className="text-lg font-bold">تحليل تقييم بيئة العمل</h1>
                <p className="text-xs text-[#6C82A3] mt-0.5">
                    {data.sources.length} مصادر · {data.axes.length} محاور · {data.totalParticipants} مشارك
                </p>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3">
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">المقيمون</span>
                    <b className="block text-xl font-semibold mt-1">{data.totalParticipants}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">المحاور</span>
                    <b className="block text-xl font-semibold mt-1">{data.axes.length}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">المتوسط المرجّح</span>
                    <b className="block text-xl font-semibold mt-1" style={{ color: scoreColor(data.overall ?? 0) }}>{fmt(data.overall)}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">أضعف محور</span>
                    {data.worstAxis ? (
                        <b
                            className="block text-[11px] font-semibold mt-1.5 leading-snug truncate"
                            style={{ color: scoreColor(data.axisWavg[data.worstAxis.key]) }}
                            title={data.worstAxis.label}
                        >
                            {data.worstAxis.label} · {fmt(data.axisWavg[data.worstAxis.key])}
                        </b>
                    ) : (
                        <b className="block text-xl font-semibold mt-1">—</b>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Donut: share of participants by source */}
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3">
                    <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">نسبة كل مصدر</h2>
                    <div className="flex flex-col items-center gap-3">
                        <svg width="112" height="112" viewBox="0 0 112 112">
                            <g transform="translate(56,56) rotate(-90)">
                                {data.sources.map((s) => {
                                    const n = data.bySourceCount[s.key] || 0;
                                    if (!n) return null;
                                    const C = 2 * Math.PI * 42;
                                    const len = (n / data.totalParticipants) * C;
                                    const el = (
                                        <circle
                                            key={s.key}
                                            r="42"
                                            fill="none"
                                            stroke={s.color}
                                            strokeWidth="15"
                                            strokeDasharray={`${Math.max(len - 2, 0)} ${C - len + 2}`}
                                            strokeDashoffset={-ringOffset}
                                        />
                                    );
                                    ringOffset += len;
                                    return el;
                                })}
                            </g>
                            <text x="56" y="54" textAnchor="middle" fill="#EAF1FA" fontSize="18" fontWeight="600">
                                {data.totalParticipants}
                            </text>
                            <text x="56" y="67" textAnchor="middle" fill="#6C82A3" fontSize="8">مشارك</text>
                        </svg>
                        <div className="w-full flex flex-col gap-1">
                            {data.sources.map((s) => {
                                const n = data.bySourceCount[s.key] || 0;
                                return (
                                    <div key={s.key} className="grid grid-cols-[8px_1fr_auto] items-center gap-2 text-[11px]">
                                        <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
                                        <span className="text-[#9DB2CE] truncate">{s.label}</span>
                                        <span className="font-semibold">
                                            {roundNumber((n / data.totalParticipants) * 100)}% <u className="no-underline text-[#6C82A3] font-normal">({n})</u>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Source averages */}
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3">
                    <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                        متوسط تقييم بيئة العمل حسب المصدر <em className="text-[10px] text-[#6C82A3] font-normal">وعدد المشاركين — اضغط لعرض النماذج</em>
                    </h2>
                    <div className="flex flex-col gap-2">
                        {data.sources.slice().sort((a, b) => (data.sourceAvg[b.key] ?? 0) - (data.sourceAvg[a.key] ?? 0)).map((s) => {
                            const isSel = selectedSource === s.key;
                            return (
                                <div
                                    key={s.key}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedSource(isSel ? null : s.key)}
                                    className={`cursor-pointer rounded-md p-1 -m-1 border ${isSel ? "bg-[#26385A] border-[#33507E]" : "border-transparent hover:bg-[#26385A]/60"}`}
                                >
                                    <div className="flex items-baseline gap-1 text-[11px] mb-1">
                                        <b className="font-medium">{s.label}</b>
                                        <span className="text-[#6C82A3] text-[10px]">{data.bySourceCount[s.key] || 0} مشارك</span>
                                        <span className="mr-auto font-semibold" style={{ color: s.color }}>{fmt(data.sourceAvg[s.key])}</span>
                                    </div>
                                    <div className="h-[9px] rounded-sm bg-[#26385A] overflow-hidden">
                                        <div
                                            className="h-full rounded-sm"
                                            style={{ width: `${data.sourceAvg[s.key] || 0}%`, background: s.color }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {data.hasMgmtGap && (
                        <p className="text-[9.5px] text-[#6C82A3] leading-relaxed mt-2">
                            الإدارة تقيّم بيئة العمل بـ{fmt(data.mgmtOverallAvg)} بينما الفريق يقيّمها بـ{fmt(data.staffOverallAvg)}
                            {data.mgmtOverallAvg != null && data.staffOverallAvg != null && (
                                <> — فارق {roundNumber(data.mgmtOverallAvg - data.staffOverallAvg)} نقطة.</>
                            )}
                        </p>
                    )}
                </section>
            </div>

            {/* Axis x Source matrix */}
            <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                    المحاور حسب مصادر التقييم <em className="text-[10px] text-[#6C82A3] font-normal">مرتبة تنازليًا حسب المتوسط المرجّح</em>
                </h2>

                <div className="overflow-x-auto">
                    <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
                        <thead>
                            <tr>
                                <th className="text-[9px] font-medium text-[#6C82A3] text-right w-32">المحور</th>
                                {data.sources.map((s) => (
                                    <th key={s.key} className="text-[9px] font-medium text-[#9DB2CE] w-12">{s.label}<br />({data.bySourceCount[s.key] || 0})</th>
                                ))}
                                <th className="text-[9px] font-medium text-[#9DB2CE] w-12">المتوسط</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.axes.map((a) => (
                                <tr key={a.key}>
                                    <td className="text-[10px] text-right pe-1 truncate max-w-[130px]">{a.label}</td>
                                    {data.sources.map((s) => {
                                        const v = avg(data.bySourceAxis[s.key]?.[a.key]);
                                        return (
                                            <td key={s.key}>
                                                {v == null ? (
                                                    <div className="h-7 rounded-md flex items-center justify-center text-[#4E627E] bg-white/[0.02]">—</div>
                                                ) : (
                                                    <div
                                                        className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold"
                                                        style={{
                                                            background: `${scoreColor(v)}22`,
                                                            color: scoreColor(v),
                                                            boxShadow: v < WEAK_THRESHOLD ? `inset 0 0 0 1.5px ${scoreColor(v)}` : "none",
                                                        }}
                                                    >
                                                        {roundNumber(v)}%
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <div
                                            className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold bg-[#26385A] border border-[#33507E]"
                                            style={{ color: scoreColor(data.axisWavg[a.key]) }}
                                        >
                                            {fmt(data.axisWavg[a.key])}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-wrap gap-3 text-[10px] text-[#9DB2CE] mt-2">
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#22B07D" }} />٩٠+</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#2E93CC" }} />٨٠–٨٩</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#E0A32B" }} />٧٠–٧٩</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#D8543F" }} />أقل من {WEAK_THRESHOLD} · نقطة ضعف</span>
                    <span className="text-[#6C82A3]">— = المحور غير مشمول لهذا المصدر</span>
                </div>
            </section>

            {/* Management vs staff gap */}
            {data.hasMgmtGap && data.gaps.length > 0 && (
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                    <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                        فجوة الإدارة والفريق <em className="text-[10px] text-[#6C82A3] font-normal">لكل محور من محاور بيئة العمل</em>
                    </h2>

                    <div className="flex flex-col">
                        {data.gaps.map((g) => {
                            const lo = Math.min(g.mgmt, g.staff);
                            const hi = Math.max(g.mgmt, g.staff);
                            const gapColor = g.gap >= 15 ? "#D8543F" : g.gap >= 8 ? "#E0A32B" : "#6C82A3";
                            return (
                                <div key={g.key} className="grid grid-cols-[104px_1fr_36px] items-center gap-2 p-[3px] rounded-md hover:bg-[#1C2C47]">
                                    <span className="text-[10px] text-[#9DB2CE] truncate" title={g.label}>{g.label}</span>
                                    <div className="relative h-[13px]">
                                        <span className="absolute block h-[2px] bg-[#33507E] rounded-sm" style={{ top: 6, right: `${lo}%`, width: `${hi - lo}%` }} />
                                        <span className="absolute block w-2 h-2 rounded-full border-[1.5px] border-[#1C2C47]" style={{ top: 3, right: `${g.staff}%`, transform: "translateX(50%)", background: "#2E93CC" }} />
                                        <span className="absolute block w-2 h-2 rounded-full border-[1.5px] border-[#1C2C47]" style={{ top: 3, right: `${g.mgmt}%`, transform: "translateX(50%)", background: "#8B7CE8" }} />
                                    </div>
                                    <span className="text-[10px] font-semibold text-left" style={{ color: gapColor }}>{g.gap > 0 ? "+" : ""}{roundNumber(g.gap)}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="relative h-3 mt-1 grid grid-cols-[104px_1fr_36px] gap-2 border-t border-[#26385A]">
                        <div />
                        <div className="relative">
                            {[0, 50, 100].map((p) => (
                                <span key={p} className="absolute top-0 text-[9px] text-[#6C82A3]" style={{ right: `${p}%`, transform: "translateX(50%)" }}>{p}%</span>
                            ))}
                        </div>
                        <div />
                    </div>

                    <div className="flex flex-wrap gap-3 text-[9.5px] text-[#9DB2CE] mt-2">
                        <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#8B7CE8" }} />متوسط الإدارة</span>
                        <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#2E93CC" }} />متوسط الفريق</span>
                        <span className="text-[#6C82A3]">موجب = الإدارة تقيّم المحور أعلى</span>
                    </div>
                </section>
            )}

            {/* Forms for the selected source */}
            {selectedSourceObj && (
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 rounded-sm" style={{ background: selectedSourceObj.color }} />
                        <h2 className="text-[13px] font-semibold text-[#9DB2CE]">
                            نماذج {selectedSourceObj.label}
                            <em className="text-[10px] text-[#6C82A3] font-normal ms-1">({selectedForms.length} نموذج)</em>
                        </h2>
                        <button
                            type="button"
                            onClick={() => setSelectedSource(null)}
                            className="mr-auto text-[10px] text-[#6C82A3] hover:text-[#EAF1FA]"
                        >
                            إغلاق ✕
                        </button>
                    </div>

                    <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto pe-1">
                        {selectedForms.map((f) => {
                            const d = f.date ? new Date(f.date) : null;
                            const dateLabel = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("ar-EG") : "—";
                            return (
                                <div
                                    key={f.id}
                                    className="grid grid-cols-[1fr_auto_56px] items-center gap-2 rounded-md border border-[#26385A] bg-[#16233A] px-2 py-1.5"
                                >
                                    <span className="text-[11px] text-[#EAF1FA] truncate">{f.name}</span>
                                    <span className="text-[10px] text-[#6C82A3]">{dateLabel}</span>
                                    <span
                                        className="text-[11px] font-semibold text-center rounded-md py-0.5"
                                        style={{ background: `${scoreColor(f.score)}22`, color: scoreColor(f.score) }}
                                    >
                                        {roundNumber(f.score)}%
                                    </span>
                                </div>
                            );
                        })}
                        {!selectedForms.length && (
                            <p className="text-[11px] text-[#6C82A3] text-center py-4">لا توجد نماذج لهذا المصدر.</p>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

export default WorkEnvDashboard;
